import {
    useContext, useEffect, useState
} from 'react';
import AuthenticateEmail from 'builders/builderChannel/components/AuthenticateEmail';
import {
    useLocation, useNavigate, useParams, useSearchParams
} from 'react-router-dom';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Button
} from 'shared/components/ui/button';
import {
    PageContent,
    PageContentHeader,
    PageContentHeaderText,
    PageContentHeaderTitle,
    PageDescription,
    PageHeader,
    PageHeaderAction,
    PageLayout,
    PageSubTitle,
    PageTitle,
    PageTitles,
} from 'shared/components/NewDesignComponents/PageLayout';
import ChannelCard from 'builders/builderChannel/components/ChannelCard';
import {
    SearchBar
} from 'builders/builderInternal/components/Instructions/Filters';
import {
    Channel
} from '../types/builderChannel.types';
import { PaginationBar } from 'shared/components/NewDesignComponents/PaginationBar';
import { channelService } from 'shared/api';
import { cn } from 'shared/lib/utils';

const ITEMS_PER_PAGE = 12;

const CHIP_META: Record<string, { color: string; label: string }> = {
    Email: { color: '#3b82f6', label: 'Email' },
    Webchat: { color: '#10b981', label: 'Web Chat' },
    WhatsApp: { color: '#25D366', label: 'WhatsApp' },
    Messenger: { color: '#0084FF', label: 'Messenger' },
    Voice: { color: '#f59e0b', label: 'Voice' },
};

const FILTER_TYPES: { value: string }[] = [
    { value: '' },
    { value: 'Email' },
    { value: 'Webchat' },
    { value: 'WhatsApp' },
    { value: 'Messenger' },
    { value: 'Voice' },
];

export default function ChannelPage() {
    const [emailChannels, setEmailChannels] = useState([] as Channel[]);
    const [channelDialog, setChannelDialog] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectType, setSelectType] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [serverTotalPages, setServerTotalPages] = useState(1);

    const { oragID, subOragID } = useParams();
    const { addNewNotifcation } = useContext(Context);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const success = searchParams.get('email-auth-success');
    const statusCode = searchParams.get('statusCode');

    const fetchEmailChannels = async (page: number) => {
        try {
            const res = await channelService.getChannels(oragID, subOragID, page, ITEMS_PER_PAGE);
            const payload = res.data;
            if (payload && typeof payload === 'object' && 'data' in payload && 'pagination' in payload) {
                const p = payload as { data: any[]; pagination: { totalPages: number } };
                setEmailChannels(p.data);
                setServerTotalPages(p.pagination.totalPages);
            } else {
                setEmailChannels(Array.isArray(payload) ? payload : []);
            }
        } catch (error) {
            console.error('error: ', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmailChannels(1);
        if (success === 'false') {
            if (statusCode === '403') {
                addNewNotifcation('Error. Please check your email configuration', 'danger');
            } else if (statusCode === '409') {
                addNewNotifcation('Error. Email already subscribed', 'danger');
            } else {
                addNewNotifcation('Something went wrong. Please try again', 'danger');
            }
            navigate(location.pathname, { replace: true });
        } else if (success === 'true') {
            setChannelDialog(true);
        }
    }, []);

    useEffect(() => {
        if (currentPage > 1) {
            setLoading(true);
            fetchEmailChannels(currentPage);
        }
    }, [currentPage]);

    const getTypeCount = (filterValue: string): number => {
        if (!filterValue) return emailChannels.length;
        if (filterValue === 'WhatsApp') return emailChannels.filter(ch => ch.type === 'WhatsApp' || ch.type === 'Whatsapp').length;
        if (filterValue === 'Webchat') return emailChannels.filter(ch => ch.type === 'Webchat' || ch.type === 'Web Chat').length;
        return emailChannels.filter(ch => ch.type === filterValue).length;
    };

    const getFilteredList = (): Channel[] => {
        return emailChannels.filter((channel) => {
            const matchesSearch =
                channel.topic?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                channel._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                channel.type?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType =
                !selectType ||
                (selectType === 'WhatsApp' && (channel.type === 'WhatsApp' || channel.type === 'Whatsapp')) ||
                (selectType === 'Webchat' && (channel.type === 'Webchat' || channel.type === 'Web Chat')) ||
                channel.type === selectType;
            return matchesSearch && matchesType;
        });
    };

    const handleSearch = (value: string): void => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    return (
        <PageLayout>
            <PageHeader>
                <PageTitles>
                    <PageTitle>Connect your Enterprise</PageTitle>
                    <PageSubTitle>Engagement Channels</PageSubTitle>
                    <PageDescription>
                        Unify and optimize your communication channels with AI-driven engagement. Seamlessly connect with
                        customers, partners, and teams to deliver personalized, impactful interactions across your enterprise.
                    </PageDescription>
                </PageTitles>
                <PageHeaderAction>
                    <Button onClick={() => setChannelDialog(true)} className="w-full">
                        Add new channel
                    </Button>
                </PageHeaderAction>
            </PageHeader>
            <PageContent loading={loading}>
                <PageContentHeader>
                    <PageContentHeaderTitle>Channel Builder:</PageContentHeaderTitle>
                    <PageContentHeaderText>Manage your Engagement Channels</PageContentHeaderText>
                </PageContentHeader>

                <div className="w-full mx-auto p-2 md:p-6">
                    <div className="space-y-4">

                        {/* Search + filter chips */}
                        <div className="flex flex-col gap-3">
                            <SearchBar onSearch={handleSearch} />
                            <div className="flex items-center gap-2 flex-wrap">
                                {FILTER_TYPES.map(({ value }) => {
                                    const chipMeta = value ? CHIP_META[value] : null;
                                    const isActive = selectType === value;
                                    const label = chipMeta?.label ?? 'All';
                                    return (
                                        <button
                                            key={value || 'all'}
                                            type="button"
                                            onClick={() => { setSelectType(value); setCurrentPage(1); }}
                                            className={cn(
                                                'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border transition-all duration-150 cursor-pointer',
                                                isActive
                                                    ? 'border-transparent text-white shadow-sm'
                                                    : 'border-border bg-background text-muted-foreground hover:text-foreground'
                                            )}
                                            style={
                                                isActive
                                                    ? {
                                                        backgroundColor: chipMeta?.color ?? 'hsl(var(--foreground))',
                                                        borderColor: chipMeta?.color ?? 'hsl(var(--foreground))',
                                                    }
                                                    : {}
                                            }
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {(() => {
                            const filteredList = getFilteredList();
                            return filteredList.length > 0 ? (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {filteredList.map((item) => (
                                            <ChannelCard key={item._id} channel={item} setEmailChannels={setEmailChannels} />
                                        ))}
                                    </div>
                                    <PaginationBar currentPage={currentPage} totalPages={serverTotalPages} onPageChange={setCurrentPage} />
                                </>
                            ) : (
                                <div className="flex flex-grow justify-center items-center py-16">
                                    <span className="text-sm text-muted-foreground">No channels found</span>
                                </div>
                            );
                        })()}
                    </div>
                </div>
                <AuthenticateEmail
                    isOpen={channelDialog}
                    setOpenEmailModal={setChannelDialog}
                    setEmailChannels={setEmailChannels}
                />
            </PageContent>
        </PageLayout>
    );
}
